import React, { useState } from "react";
import {
  ArrowDownLeft,
  ArrowUpRight,
  Check,
  ChevronDown,
  Clock3,
  Copy,
  Minus,
  XCircle,
} from "lucide-react";
import { formatAgo } from "../../utils/activityHelpers";
import {
  buildTxDetailRows,
  formatBalance,
  txAmountParts,
  txCurrencyLabel,
  txStatusLabel,
  txTypeLabel,
} from "../../utils/dashboardHelpers";
import {
  PENDING_DEPOSIT_APPROVAL_NOTE,
  shouldShowPendingDepositApprovalNote,
} from "../../utils/paymentHelpers";

const FULL_WIDTH_LABELS = new Set(["Description", "Hold reason", "Reference"]);

const FLOW_ICON = {
  in: ArrowDownLeft,
  out: ArrowUpRight,
  pending: Clock3,
  failed: XCircle,
  neutral: Minus,
};

const txFlow = (tone, status = "") => {
  const s = String(status).toLowerCase();
  if (["pending", "hold", "onhold"].includes(s)) return { key: "pending", label: "Pending" };
  if (["failed", "cancelled", "canceled"].includes(s)) return { key: "failed", label: "Failed" };
  if (tone === "credit") return { key: "in", label: "In" };
  if (tone === "debit") return { key: "out", label: "Out" };
  return { key: "neutral", label: "Neutral" };
};

const DetailGridCell = ({ row, copiedField, onCopy }) => {
  const toneClass = row.alert ? "alert" : row.highlight ? "highlight" : "default";
  const wide = FULL_WIDTH_LABELS.has(row.label);

  return (
    <li
      className={`ra-stream-detail-cell ra-stream-detail-${toneClass}${wide ? " ra-stream-detail-cell-wide" : ""}`}
    >
      <em className="ra-stream-detail-label">{row.label}</em>
      <span className="ra-stream-detail-value-row">
        <strong className="ra-stream-detail-value">{row.value || "—"}</strong>
        {row.copyable && row.value ? (
          <button
            type="button"
            className="ra-stream-copy ra-stream-detail-copy"
            onClick={() => onCopy(row.label, row.value)}
            aria-label={`Copy ${row.label}`}
          >
            {copiedField === row.label ? <Check size={11} /> : <Copy size={11} />}
          </button>
        ) : null}
      </span>
    </li>
  );
};

const DashboardTransactionRow = ({ tx, expanded, onToggle, isLast = false }) => {
  const [copiedField, setCopiedField] = useState(null);
  const { prefix, amount, tone } = txAmountParts(tx);
  const flow = txFlow(tone, tx.status);
  const FlowIcon = FLOW_ICON[flow.key] || Minus;
  const detailRows = expanded ? buildTxDetailRows(tx) : [];
  const showApprovalNote = shouldShowPendingDepositApprovalNote(tx);
  const status = String(tx.status || "").toLowerCase();
  const showStatus = ["pending", "hold", "onhold", "failed", "cancelled", "canceled"].includes(status);
  const currency = txCurrencyLabel(tx.currency);

  const copyValue = async (label, value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(String(value));
      setCopiedField(label);
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <li className={`ra-stream-item ra-stream-${flow.key}${expanded ? " ra-stream-item-open" : ""}`}>
      <button type="button" className="ra-stream-btn" onClick={onToggle} aria-expanded={expanded}>
        <span className="ra-stream-rail" aria-hidden>
          <span className="ra-stream-dot" />
          {!isLast || expanded ? <span className="ra-stream-line" /> : null}
        </span>

        <span className={`ra-stream-flow ra-stream-flow-${flow.key}`} title={flow.label}>
          <FlowIcon size={12} />
        </span>

        <span className="ra-stream-body">
          <span className="ra-stream-top">
            <strong>{txTypeLabel(tx.transactionType)}</strong>
            {showStatus ? (
              <em className={`ra-stream-flag ra-stream-flag-${flow.key}`}>
                {txStatusLabel(tx.status)}
              </em>
            ) : null}
          </span>
          <span className="ra-stream-meta">
            {formatAgo(tx.createdAt)}
            {currency ? ` · ${currency}` : ""}
          </span>
        </span>

        <span className="ra-stream-amt tabular-nums">
          {prefix}
          {formatBalance(amount, { compact: amount >= 10000 })}
        </span>

        <ChevronDown
          size={13}
          className={`ra-stream-chevron ra-stream-chevron-expand${expanded ? " ra-stream-chevron-open" : ""}`}
          aria-hidden
        />
      </button>

      {expanded && (
        <div className="ra-stream-expand">
          {showApprovalNote && (
            <div className="ra-stream-detail-note">
              <span className="ra-stream-detail-note-icon" aria-hidden>
                <Clock3 size={12} />
              </span>
              <p>{PENDING_DEPOSIT_APPROVAL_NOTE}</p>
            </div>
          )}

          <ul className="ra-stream-detail-grid">
            {detailRows.map((row) => (
              <DetailGridCell
                key={row.label}
                row={row}
                copiedField={copiedField}
                onCopy={copyValue}
              />
            ))}
          </ul>
        </div>
      )}
    </li>
  );
};

export default DashboardTransactionRow;
